/* ─── Jarvis Voice Console - Main Application Entry Point ─── */
/* Security Note: All innerHTML usages below render only trusted templates or
   content passed through escapeHtml(). No unsanitized user input reaches innerHTML. */
import { ViewManager, VoiceOrb } from './components.js';
import { ApiClient } from './api.js';

/* ─── State Management ─── */
const state = {
  view: 'home',
  voiceActive: false,
  currentSessionId: null,
  userLocation: null,
  lastBriefing: null
};

/* ─── Initialize Core Services ─── */
const viewManager = new ViewManager();
const api = new ApiClient('https://jarvis-voice.tahsin-oezdemir.workers.dev');
let voiceOrb = null;

/* ─── DOM Elements (populated after view load) ─── */
let elements = {};

/* ─── Navigation Handler ─── */
function setupNavigation() {
  // Primary nav items
  document.querySelectorAll('.primary-nav-item[data-view]').forEach(el => {
    el.addEventListener('click', () => navigate(el.dataset.view));
  });

  // Menu dropdown items
  document.querySelectorAll('.menu-item[data-view]').forEach(el => {
    el.addEventListener('click', () => {
      navigate(el.dataset.view);
      closeMenu();
    });
  });

  // Brand home button
  const brandHome = document.querySelector('.brand-home');
  if (brandHome) {
    brandHome.addEventListener('click', () => navigate('home'));
  }

  // View-specific nav buttons (overview cards, etc.)
  document.querySelectorAll('[data-view]').forEach(el => {
    if (!el.classList.contains('primary-nav-item') && !el.classList.contains('menu-item')) {
      el.addEventListener('click', () => navigate(el.dataset.view));
    }
  });

  // Hamburger menu
  const menuBtn = document.querySelector('.menu-btn');
  const menuDropdown = document.querySelector('.menu-dropdown');
  if (menuBtn && menuDropdown) {
    menuBtn.addEventListener('click', () => toggleMenu());
    document.addEventListener('click', (e) => {
      if (!menuBtn.contains(e.target) && !menuDropdown.contains(e.target)) {
        closeMenu();
      }
    });
  }
}

function navigate(viewName) {
  // Update primary nav active state
  document.querySelectorAll('.primary-nav-item').forEach(el => {
    el.setAttribute('aria-current', el.dataset.view === viewName ? 'page' : 'false');
  });

  // Update menu active state
  document.querySelectorAll('.menu-item[data-view]').forEach(el => {
    el.classList.toggle('active', el.dataset.view === viewName);
  });

  viewManager.showView(viewName).then(() => {
    state.view = viewName;
    initializeView(viewName);
  });
}

function toggleMenu() {
  const menuBtn = document.querySelector('.menu-btn');
  const menuDropdown = document.querySelector('.menu-dropdown');
  if (!menuBtn || !menuDropdown) return;

  const isOpen = !menuDropdown.hasAttribute('hidden');
  menuDropdown.hidden = isOpen;
  menuBtn.setAttribute('aria-expanded', !isOpen);
}

function closeMenu() {
  const menuBtn = document.querySelector('.menu-btn');
  const menuDropdown = document.querySelector('.menu-dropdown');
  if (!menuBtn || !menuDropdown) return;
  menuDropdown.hidden = true;
  menuBtn.setAttribute('aria-expanded', 'false');
}

/* ─── View Initialization ─── */
function initializeView(viewName) {
  // Re-query elements after view load
  cacheElements();

  // Initialize voice orb if present in this view
  const orbEl = document.querySelector('.voice-panel');
  if (orbEl && !voiceOrb) {
    voiceOrb = new VoiceOrb(orbEl);
    setupVoiceOrbEvents();
  }

  // View-specific initialization
  switch (viewName) {
    case 'home':
      initHomeView();
      break;
    case 'heute':
      initHeuteView();
      break;
    case 'wetter':
      initWetterView();
      break;
    case 'chat':
      initChatView();
      break;
    case 'statistik':
      initStatistikView();
      break;
    case 'chronik':
      initChronikView();
      break;
    case 'briefing':
      initBriefingView();
      break;
    case 'notizen':
      initNotizenView();
      break;
  }

  // Common: refresh buttons
  document.querySelectorAll('[id$="-refresh"]').forEach(btn => {
    btn.addEventListener('click', () => refreshView(viewName));
  });

  // Today action buttons (send prompt to voice/chat)
  document.querySelectorAll('.today-action[data-prompt]').forEach(btn => {
    btn.addEventListener('click', () => {
      const prompt = btn.dataset.prompt;
      if (state.view === 'chat') {
        sendChatMessage(prompt);
      } else if (voiceOrb) {
        voiceOrb.addMessage('user', prompt);
        // Trigger voice processing
        voiceOrb.setState('processing');
      }
    });
  });
}

function cacheElements() {
  elements = {
    orb: document.getElementById('orb'),
    transcript: document.getElementById('transcript'),
    startBtn: document.getElementById('start-button'),
    stopBtn: document.getElementById('stop-button'),
    textForm: document.getElementById('text-form'),
    textInput: document.getElementById('text-message'),
    errorMsg: document.getElementById('error-message'),
    clearBtn: document.getElementById('clear-button'),
    modeLabel: document.getElementById('mode-label'),
    locationLive: document.getElementById('location-live'),
    locationModal: document.getElementById('location-modal'),
    locationClose: document.getElementById('location-close'),
    locationMap: document.getElementById('location-map'),
    locationStatus: document.getElementById('location-status'),
    locationOpen: document.getElementById('location-open')
  };
}

/* ─── Voice Orb Event Handling ─── */
function setupVoiceOrbEvents() {
  if (!voiceOrb) return;

  // Start button
  if (elements.startBtn) {
    elements.startBtn.addEventListener('click', () => startVoice());
  }

  // Stop button
  if (elements.stopBtn) {
    elements.stopBtn.addEventListener('click', () => stopVoice());
  }

  // Text form
  if (elements.textForm) {
    elements.textForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = elements.textInput.value.trim();
      if (text) {
        sendTextMessage(text);
        elements.textInput.value = '';
      }
    });
  }

  // Clear transcript
  if (elements.clearBtn) {
    elements.clearBtn.addEventListener('click', () => {
      voiceOrb.clearTranscript();
    });
  }

  // Location modal
  if (elements.locationLive) {
    elements.locationLive.addEventListener('click', () => openLocationModal());
  }
  if (elements.locationClose) {
    elements.locationClose.addEventListener('click', () => closeLocationModal());
  }
  if (elements.locationModal) {
    elements.locationModal.addEventListener('click', (e) => {
      if (e.target === elements.locationModal) closeLocationModal();
    });
  }

  // Voice orb events
  voiceOrb.element.addEventListener('voice:start', handleVoiceStart);
  voiceOrb.element.addEventListener('voice:stop', handleVoiceStop);
}

async function startVoice() {
  if (!voiceOrb) return;

  elements.startBtn.disabled = true;
  elements.stopBtn.disabled = false;
  elements.errorMsg.textContent = '';

  try {
    voiceOrb.setState('listening');
    const response = await api.startVoiceSession();
    state.currentSessionId = response.sessionId;
  } catch (error) {
    elements.errorMsg.textContent = `Fehler: ${error.message}`;
    voiceOrb.setState('idle');
    elements.startBtn.disabled = false;
    elements.stopBtn.disabled = true;
  }
}

async function stopVoice() {
  if (!voiceOrb || !state.currentSessionId) return;

  elements.stopBtn.disabled = true;

  try {
    await api.stopVoiceSession();
    voiceOrb.setState('idle');
    state.currentSessionId = null;
  } catch (error) {
    elements.errorMsg.textContent = `Fehler: ${error.message}`;
  } finally {
    elements.startBtn.disabled = false;
    elements.stopBtn.disabled = true;
  }
}

function handleVoiceStart() {
  console.log('Voice session started');
}

async function handleVoiceStop() {
  console.log('Voice session stopped');
  voiceOrb.setState('idle');
  elements.startBtn.disabled = false;
  elements.stopBtn.disabled = true;
}

function sendTextMessage(text) {
  if (!voiceOrb) return;

  voiceOrb.addMessage('user', text);
  voiceOrb.setState('processing');

  // Send to chat API
  api.post('/api/chat', { message: text, sessionId: state.currentSessionId })
    .then(response => {
      if (response.reply) {
        voiceOrb.addMessage('agent', response.reply);
      }
      voiceOrb.setState('listening');
    })
    .catch(error => {
      elements.errorMsg.textContent = `Fehler: ${error.message}`;
      voiceOrb.setState('listening');
    });
}

function sendChatMessage(text) {
  // For chat view
  const feed = document.getElementById('chat-feed');
  if (!feed) return;

  // Add user message
  const userMsg = document.createElement('div');
  userMsg.className = 'message user';
  userMsg.innerHTML = `<strong>DU</strong><span>${escapeHtml(text)}</span>`;
  feed.appendChild(userMsg);

  // Send to API
  api.post('/api/chat', { message: text, sessionId: state.currentSessionId })
    .then(response => {
      if (response.reply) {
        const agentMsg = document.createElement('div');
        agentMsg.className = 'message agent';
        agentMsg.innerHTML = `<strong>JARVIS</strong><span>${escapeHtml(response.reply)}</span>`;
        feed.appendChild(agentMsg);
        feed.scrollTop = feed.scrollHeight;
      }
    })
    .catch(error => {
      console.error('Chat error:', error);
    });
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/* ─── Location Modal ─── */
function openLocationModal() {
  if (!elements.locationModal) return;
  elements.locationModal.hidden = false;
  elements.locationModal.setAttribute('aria-hidden', 'false');
  elements.locationStatus.textContent = 'Lade Standort …';
  elements.locationMap.src = '';

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords;
      state.userLocation = { lat: latitude, lon: longitude };
      elements.locationMap.src = `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.01},${latitude - 0.01},${longitude + 0.01},${latitude + 0.01}&layer=mapnik&marker=${latitude},${longitude}`;
      elements.locationStatus.textContent = `Standort: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      elements.locationOpen.href = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`;
    },
    (err) => {
      elements.locationStatus.textContent = `Fehler: ${err.message}`;
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

function closeLocationModal() {
  if (!elements.locationModal) return;
  elements.locationModal.hidden = true;
  elements.locationModal.setAttribute('aria-hidden', 'true');
  elements.locationMap.src = '';
}

/* ─── View-Specific Initializers ─── */
async function initHomeView() {
  await Promise.all([
    loadOverviewData(),
    loadNews('all'),
    updateClock()
  ]);

  // Start clock interval
  if (!window.clockInterval) {
    window.clockInterval = setInterval(updateClock, 1000);
  }
}

async function loadOverviewData() {
  try {
    const [briefing, weather, calendar] = await Promise.allSettled([
      api.getBriefing(),
      loadWeatherForOverview(),
      api.getCalendar()
    ]);

    if (briefing.status === 'fulfilled') {
      updateOverviewWeather(briefing.value);
    }
    if (weather.status === 'fulfilled') {
      updateOverviewWeather(weather.value);
    }
    if (calendar.status === 'fulfilled') {
      updateNextEvent(calendar.value);
    }
  } catch (error) {
    console.error('Overview data load error:', error);
  }
}

async function loadWeatherForOverview() {
  if (!state.userLocation) {
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
      });
      state.userLocation = { lat: pos.coords.latitude, lon: pos.coords.longitude };
    } catch {
      return null;
    }
  }
  return api.getWeather(state.userLocation.lat, state.userLocation.lon);
}

function updateOverviewWeather(data) {
  const tempEl = document.getElementById('weather-temp');
  const descEl = document.getElementById('weather-desc');
  const subEl = document.getElementById('weather-sub');
  const cockpitTemp = document.getElementById('weather-temp');
  const cockpitDesc = document.getElementById('weather-desc');
  const cockpitSub = document.getElementById('weather-sub');

  if (data?.current) {
    const temp = `${Math.round(data.current.temperature)}°C`;
    const desc = data.current.description || 'Unbekannt';
    const sub = `Feuchtigkeit: ${data.current.humidity}% · Wind: ${data.current.wind_speed} km/h`;

    if (tempEl) tempEl.textContent = temp;
    if (descEl) descEl.textContent = desc;
    if (subEl) subEl.textContent = sub;
    if (cockpitTemp) cockpitTemp.textContent = temp;
    if (cockpitDesc) cockpitDesc.textContent = desc;
    if (cockpitSub) cockpitSub.textContent = sub;
  }
}

function updateNextEvent(calendarData) {
  const el = document.getElementById('overview-next-event');
  const todayEl = document.getElementById('today-calendar');
  if (!el && !todayEl) return;

  const events = calendarData?.events || [];
  const now = new Date();
  const next = events.find(e => new Date(e.end) > now) || events[0];
  const text = next ? `${next.title} · ${formatTime(new Date(next.start))}` : 'Keine Termine';

  if (el) el.textContent = text;
  if (todayEl) todayEl.textContent = text;
}

function formatTime(date) {
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function updateClock() {
  const now = new Date();
  const timeEl = document.getElementById('cockpit-time');
  if (timeEl) {
    timeEl.textContent = now.toLocaleString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}

async function loadNews(category = 'all') {
  const listEl = document.getElementById('news-list');
  if (!listEl) return;

  listEl.innerHTML = '<div class="empty-state"><span>◌</span><p>News werden geladen …</p></div>';

  try {
    const data = await api.getNews(category);
    renderNews(data.articles || [], listEl);
  } catch (error) {
    listEl.innerHTML = `<div class="empty-state"><span>⚠</span><p>Fehler: ${error.message}</p></div>`;
  }
}

function renderNews(articles, container) {
  if (!articles.length) {
    container.innerHTML = '<div class="news-empty">Keine Nachrichten gefunden.</div>';
    return;
  }

  container.innerHTML = articles.map(a => `
    <article class="news-item">
      <div class="n-meta">
        <span class="n-cat">${a.category || 'Allgemein'}</span>
        <span class="n-src">${a.source || 'Unbekannt'}</span>
      </div>
      <a class="n-title" href="${a.url}" target="_blank" rel="noopener">${escapeHtml(a.title)}</a>
      <p>${escapeHtml(a.description || '')}</p>
    </article>
  `).join('');
}

async function initHeuteView() {
  try {
    const today = await api.getToday();
    const el = document.getElementById('today-calendar');
    if (el && today?.events) {
      el.textContent = today.events.length ? today.events[0].title : 'Keine Termine';
    }
  } catch (error) {
    console.error('Heute load error:', error);
  }
}

async function initWetterView() {
  // Voice orb already initialized, just load weather if needed
  if (state.userLocation) {
    const weather = await api.getWeather(state.userLocation.lat, state.userLocation.lon);
    updateOverviewWeather(weather);
  }
}

async function initChatView() {
  // Load existing sessions
  loadChatSessions();

  // Chat form
  const form = document.getElementById('chat-form');
  const input = document.getElementById('chat-input');
  const feed = document.getElementById('chat-feed');

  if (form && input) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text) return;

      // Add user message
      const userMsg = document.createElement('div');
      userMsg.className = 'message user';
      userMsg.innerHTML = `<strong>DU</strong><span>${escapeHtml(text)}</span>`;
      feed.appendChild(userMsg);
      feed.scrollTop = feed.scrollHeight;
      input.value = '';

      // Send to API
      try {
        const response = await api.sendChat(text, state.currentSessionId);
        if (response.reply) {
          const agentMsg = document.createElement('div');
          agentMsg.className = 'message agent';
          agentMsg.innerHTML = `<strong>JARVIS</strong><span>${escapeHtml(response.reply)}</span>`;
          feed.appendChild(agentMsg);
          feed.scrollTop = feed.scrollHeight;
        }
        if (response.sessionId) {
          state.currentSessionId = response.sessionId;
        }
      } catch (error) {
        const errorMsg = document.createElement('div');
        errorMsg.className = 'message agent';
        errorMsg.innerHTML = `<strong>FEHLER</strong><span>${escapeHtml(error.message)}</span>`;
        feed.appendChild(errorMsg);
        feed.scrollTop = feed.scrollHeight;
      }
    });
  }

  // New chat button
  const newBtn = document.getElementById('chat-new');
  if (newBtn) {
    newBtn.addEventListener('click', () => {
      state.currentSessionId = null;
      feed.innerHTML = '<div class="empty-state" style="margin:auto;"><span>◌</span><p>Neuer Chat gestartet. Schreibe eine Nachricht.</p></div>';
      loadChatSessions();
    });
  }
}

async function loadChatSessions() {
  const wrap = document.getElementById('chat-sessions-wrap');
  const grid = document.getElementById('chat-sessions');
  if (!wrap || !grid) return;

  try {
    const sessions = await api.get('/api/chat/sessions');
    if (sessions?.length) {
      wrap.style.display = 'block';
      grid.innerHTML = sessions.map(s => `
        <button class="history-item" data-session="${s.id}">
          <small>${new Date(s.updated).toLocaleString('de-DE')}</small>
          <p>${escapeHtml(s.title || 'Unbenannter Chat')}</p>
        </button>
      `).join('');

      grid.querySelectorAll('.history-item').forEach(btn => {
        btn.addEventListener('click', () => loadChatSession(btn.dataset.session));
      });
    }
  } catch (error) {
    console.error('Load sessions error:', error);
  }
}

async function loadChatSession(sessionId) {
  const feed = document.getElementById('chat-feed');
  if (!feed) return;

  state.currentSessionId = sessionId;
  feed.innerHTML = '<div class="empty-state" style="margin:auto;"><span>◌</span><p>Lade Chat …</p></div>';

  try {
    const session = await api.get(`/api/chat/sessions/${sessionId}`);
    if (session?.messages) {
      feed.innerHTML = session.messages.map(m => `
        <div class="message ${m.role}">
          <strong>${m.role === 'user' ? 'DU' : 'JARVIS'}</strong>
          <span>${escapeHtml(m.content)}</span>
        </div>
      `).join('');
      feed.scrollTop = feed.scrollHeight;
    }
  } catch (error) {
    feed.innerHTML = `<div class="empty-state" style="margin:auto;"><span>⚠</span><p>Fehler: ${error.message}</p></div>`;
  }
}

async function initStatistikView() {
  try {
    const stats = await api.getStats();
    document.getElementById('stat-sessions').textContent = stats.totalSessions || '–';
    document.getElementById('stat-last').textContent = stats.lastActivity ? new Date(stats.lastActivity).toLocaleString('de-DE') : '–';
    document.getElementById('stat-latency').textContent = stats.avgVoiceLatency ? `${stats.avgVoiceLatency}ms` : '–';
    document.getElementById('stat-chat-latency').textContent = stats.avgChatLatency ? `${stats.avgChatLatency}ms` : '–';
    document.getElementById('stat-tools').textContent = stats.toolCalls || '–';
  } catch (error) {
    console.error('Stats load error:', error);
  }

  // Also load news
  loadNews('all');
}

async function initChronikView() {
  const listEl = document.querySelector('.history-list');
  const detailEl = document.querySelector('.history-detail');

  if (!listEl) return;

  // Filter buttons
  document.querySelectorAll('.history .filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.history .filter-btn').forEach(b => b.classList.remove('selected'));
      btn.classList.add('selected');
      loadHistory(btn.dataset.filter);
    });
  });

  // Load initial history
  await loadHistory('all');
}

async function loadHistory(filter) {
  const listEl = document.querySelector('.history-list');
  const detailEl = document.querySelector('.history-detail');
  if (!listEl) return;

  listEl.innerHTML = '<div class="history-skeleton"></div><div class="history-skeleton"></div><div class="history-skeleton"></div>';

  try {
    const data = await api.getHistory(filter);
    if (!data?.items?.length) {
      listEl.innerHTML = '<div class="empty-state" style="margin:auto;padding:20px;"><span>◌</span><p>Keine Einträge gefunden.</p></div>';
      return;
    }

    listEl.innerHTML = data.items.map(item => `
      <button class="history-item${item.selected ? ' selected' : ''}" data-id="${item.id}">
        <small>${new Date(item.timestamp).toLocaleString('de-DE')}</small>
        <p>${escapeHtml(item.preview || item.content)}</p>
        <div class="badges">
          ${item.type ? `<span class="badge ${item.type}">${item.type}</span>` : ''}
          ${item.speed ? `<span class="badge ${item.speed}">${item.speed}</span>` : ''}
        </div>
      </button>
    `).join('');

    listEl.querySelectorAll('.history-item').forEach(btn => {
      btn.addEventListener('click', () => showHistoryDetail(btn.dataset.id, detailEl));
    });
  } catch (error) {
    listEl.innerHTML = `<div class="empty-state" style="margin:auto;padding:20px;"><span>⚠</span><p>Fehler: ${error.message}</p></div>`;
  }
}

async function showHistoryDetail(id, detailEl) {
  if (!detailEl) return;

  // Update active state
  document.querySelectorAll('.history-item').forEach(btn => {
    btn.classList.toggle('selected', btn.dataset.id === id);
  });

  detailEl.innerHTML = '<div class="empty-state" style="margin:auto;"><span>◌</span><p>Lade Details …</p></div>';

  try {
    const item = await api.get(`/api/history/${id}`);
    if (!item) return;

    detailEl.innerHTML = `
      <div class="meta">${new Date(item.timestamp).toLocaleString('de-DE')} · ${item.type || 'Unbekannt'} ${item.path ? `<span class="path-tag">${item.path}</span>` : ''}</div>
      ${item.messages?.map(m => `
        <div class="message ${m.role}">
          <strong>${m.role === 'user' ? 'DU' : 'JARVIS'}</strong>
          <span>${escapeHtml(m.content)}</span>
        </div>
      `).join('') || '<div class="empty-state" style="margin:auto;"><p>Keine Nachrichten in diesem Eintrag.</p></div>'}
    `;
  } catch (error) {
    detailEl.innerHTML = `<div class="empty-state" style="margin:auto;"><span>⚠</span><p>Fehler: ${error.message}</p></div>`;
  }
}

async function initBriefingView() {
  const contentEl = document.getElementById('briefing-content');
  if (!contentEl) return;

  try {
    const briefing = await api.getBriefing();
    if (briefing?.markdown) {
      contentEl.textContent = briefing.markdown;
      state.lastBriefing = briefing;
    } else {
      contentEl.innerHTML = '<div class="empty-state" style="margin:auto;"><span>◌</span><p>Kein Briefing verfügbar.</p></div>';
    }
  } catch (error) {
    contentEl.innerHTML = `<div class="empty-state" style="margin:auto;"><span>⚠</span><p>Fehler: ${error.message}</p></div>`;
  }
}

async function initNotizenView() {
  const listEl = document.getElementById('notes-list');
  if (!listEl) return;

  try {
    const notes = await api.getNotes();
    if (!notes?.length) {
      listEl.innerHTML = '<div class="empty-state" style="margin:auto;"><span>◌</span><p>Keine Notizen gefunden.</p></div>';
      return;
    }

    listEl.innerHTML = notes.map(n => `
      <article class="news-item">
        <div class="n-meta">
          <span class="n-cat">${n.type || 'Notiz'}</span>
          <span class="n-src">${new Date(n.created).toLocaleString('de-DE')}</span>
        </div>
        <p>${escapeHtml(n.content)}</p>
      </article>
    `).join('');
  } catch (error) {
    listEl.innerHTML = `<div class="empty-state" style="margin:auto;"><span>⚠</span><p>Fehler: ${error.message}</p></div>`;
  }
}

/* ─── Refresh Handler ─── */
function refreshView(viewName) {
  initializeView(viewName);
}

/* ─── Theme Handling ─── */
function initTheme() {
  const savedTheme = localStorage.getItem('theme') || 'dark';
  document.body.classList.toggle('theme-light', savedTheme === 'light');

  const themeBtn = document.querySelector('.menu-item[data-theme]');
  if (themeBtn) {
    themeBtn.addEventListener('click', () => {
      const newTheme = document.body.classList.contains('theme-light') ? 'dark' : 'light';
      document.body.classList.toggle('theme-light', newTheme === 'light');
      localStorage.setItem('theme', newTheme);
      themeBtn.querySelector('.menu-theme-icon').textContent = newTheme === 'light' ? '☀' : '🌙';
    });
  }
}

/* ─── Keyboard Shortcuts ─── */
function initKeyboardShortcuts() {
  document.addEventListener('keydown', (e) => {
    // Ctrl/Cmd + K: Focus text input
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      const input = document.getElementById('text-message') || document.getElementById('chat-input');
      if (input) input.focus();
    }

    // Escape: Close modals, stop voice
    if (e.key === 'Escape') {
      closeLocationModal();
      closeMenu();
      if (voiceOrb && voiceOrb.state !== 'idle') {
        stopVoice();
      }
    }

    // Ctrl/Cmd + Enter: Send message
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      const textInput = document.getElementById('text-message');
      const chatInput = document.getElementById('chat-input');
      if (textInput && document.activeElement === textInput) {
        textInput.form?.dispatchEvent(new Event('submit'));
      } else if (chatInput && document.activeElement === chatInput) {
        chatInput.form?.dispatchEvent(new Event('submit'));
      }
    }
  });
}

/* ─── App Bootstrap ─── */
async function bootstrap() {
  console.log('🚀 Jarvis Voice Console starting...');

  // Initialize theme
  initTheme();

  // Initialize keyboard shortcuts
  initKeyboardShortcuts();

  // Setup navigation
  setupNavigation();

  // Determine initial view from hash
  const initialView = window.location.hash.slice(1) || 'home';
  await viewManager.showView(initialView);
  state.view = initialView;
  initializeView(initialView);

  // Handle hash changes
  window.addEventListener('hashchange', () => {
    const view = window.location.hash.slice(1) || 'home';
    navigate(view);
  });

  // Load user location for weather
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
    });
    state.userLocation = { lat: pos.coords.latitude, lon: pos.coords.longitude };
  } catch {
    console.log('Geolocation not available');
  }

  console.log('✅ Jarvis Voice Console ready');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

/* ─── Global exports for debugging ─── */
window.Jarvis = {
  state,
  viewManager,
  api,
  voiceOrb: () => voiceOrb
};